// src/components/customers/CalendarEventsPopover.jsx
import { useState, useRef, useEffect } from "react";

/**
 * CalendarEventsPopover Component
 *
 * A popover component that displays calendar events in a dropdown format.
 * Shows event details with color-coded event types and quick access to event information.
 *
 * @param {Object} props - Component props
 * @param {Array} props.calendarEvents - Array of calendar event objects
 * @param {string} props.jobId - Current job ID
 */
const CalendarEventsPopover = ({ calendarEvents, jobId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef();
  const buttonRef = useRef();

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle escape key to close popover
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Format date display
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("en-GB", {
        dateStyle: "short",
        timeStyle: "short",
      });
    } catch (error) {
      return dateString;
    }
  };

  // Event type configuration with colors and icons
  const eventTypeConfig = {
    measurement: {
      label: "Measurement",
      bgColor: "bg-blue-100",
      textColor: "text-blue-800",
      borderColor: "border-blue-300",
      iconBg: "bg-blue-600",
    },
    installation: {
      label: "Installation",
      bgColor: "bg-green-100",
      textColor: "text-green-800",
      borderColor: "border-green-300",
      iconBg: "bg-green-600",
    },
    pricing: {
      label: "Pricing",
      bgColor: "bg-purple-100",
      textColor: "text-purple-800",
      borderColor: "border-purple-300",
      iconBg: "bg-purple-600",
    },
    "follow-up": {
      label: "Follow-up",
      bgColor: "bg-yellow-100",
      textColor: "text-yellow-800",
      borderColor: "border-yellow-300",
      iconBg: "bg-yellow-600",
    },
    quote: {
      label: "Quote",
      bgColor: "bg-indigo-100",
      textColor: "text-indigo-800",
      borderColor: "border-indigo-300",
      iconBg: "bg-indigo-600",
    },
    reschedule: {
      label: "Reschedule",
      bgColor: "bg-orange-100",
      textColor: "text-orange-800",
      borderColor: "border-orange-300",
      iconBg: "bg-orange-600",
    },
    pickup: {
      label: "Pickup",
      bgColor: "bg-teal-100",
      textColor: "text-teal-800",
      borderColor: "border-teal-300",
      iconBg: "bg-teal-600",
    },
    delivery: {
      label: "Delivery",
      bgColor: "bg-pink-100",
      textColor: "text-pink-800",
      borderColor: "border-pink-300",
      iconBg: "bg-pink-600",
    },
  };

  // Get event type configuration
  const getEventConfig = (eventType) => {
    return (
      eventTypeConfig[eventType] || {
        label: eventType,
        bgColor: "bg-gray-100",
        textColor: "text-gray-800",
        borderColor: "border-gray-300",
        iconBg: "bg-gray-600",
      }
    );
  };

  // Filter events for this job (events array should already be filtered, but double check)
  const jobEvents = calendarEvents || [];

  // Toggle popover visibility
  const togglePopover = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative w-full sm:w-auto">
      {/* Trigger Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={togglePopover}
        className="w-full btn flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
        title="View calendar events"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* Calendar icon */}
        <svg
          className="w-5 h-5 mr-2 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        Show Events
        {jobEvents.length > 0 && (
          <span className="ml-2 bg-purple-800 text-white text-xs px-2 py-1 rounded-full">
            {jobEvents.length}
          </span>
        )}
        {/* Dropdown arrow */}
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Popover Content */}
      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed left-4 right-4 sm:left-auto sm:right-auto sm:w-[32rem] mt-2 bg-white rounded-lg shadow-2xl border border-gray-200 z-[9999] transform transition-all duration-200"
          style={{
            top: buttonRef.current?.getBoundingClientRect().bottom + 8 + "px",
            left:
              window.innerWidth >= 640
                ? buttonRef.current?.getBoundingClientRect().right - 512 + "px"
                : "1rem",
          }}
          role="menu"
          aria-orientation="vertical"
        >
          {/* Popover Header */}
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                Calendar Events
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                aria-label="Close events popover"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <p className="text-sm text-purple-600 mt-1">
              {jobEvents.length} event{jobEvents.length !== 1 ? "s" : ""}{" "}
              scheduled
            </p>
          </div>

          {/* Popover Body */}
          <div className="max-h-96 overflow-y-auto">
            {jobEvents.length === 0 ? (
              /* Empty State */
              <div className="px-4 py-8 text-center">
                <svg
                  className="w-12 h-12 mx-auto text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-gray-500 text-sm mb-2">
                  No events scheduled
                </p>
                <p className="text-gray-400 text-xs">
                  Click "Add to Calendar" to create an event
                </p>
              </div>
            ) : (
              /* Events List */
              <div className="py-2">
                {jobEvents.map((event, index) => {
                  const config = getEventConfig(event.eventType);
                  return (
                    <div
                      key={event.id || index}
                      className={`mx-3 my-2 px-4 py-3 rounded-lg border ${config.borderColor} ${config.bgColor} transition-all duration-150 hover:shadow-md`}
                    >
                      {/* Event Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-start flex-1 min-w-0">
                          <div
                            className={`w-8 h-8 ${config.iconBg} rounded-full flex items-center justify-center mr-3 flex-shrink-0`}
                          >
                            <svg
                              className="w-4 h-4 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span
                                className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${config.textColor} bg-white bg-opacity-70`}
                              >
                                {config.label}
                              </span>
                            </div>
                            <p className="font-semibold text-gray-900 text-sm truncate">
                              {event.title}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Event Time */}
                      <div className="ml-11 space-y-1 text-sm">
                        <div className="flex items-center text-gray-700">
                          <svg
                            className="w-4 h-4 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="font-medium">Start:</span>
                          <span className="ml-1">
                            {formatDateTime(event.start)}
                          </span>
                        </div>
                        <div className="flex items-center text-gray-700">
                          <svg
                            className="w-4 h-4 mr-2 flex-shrink-0"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          <span className="font-medium">End:</span>
                          <span className="ml-1">
                            {formatDateTime(event.end)}
                          </span>
                        </div>
                      </div>

                      {/* Event Description */}
                      {event.description && (
                        <div className="ml-11 mt-2 text-sm text-gray-600">
                          <p className="line-clamp-3 whitespace-pre-wrap">
                            {event.description}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Popover Footer */}
          <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-sm bg-purple-600 text-white px-4 py-1.5 rounded hover:bg-purple-700 transition-colors duration-150"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarEventsPopover;
